import {
  createMediator,
  type Mediator,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export type R18OnboardingEvent =
  | {
      type:
        | "r18-onboarding.reset"
        | "r18-onboarding.dismiss"
        | "r18-onboarding.complete";
    }
  | { type: "r18-onboarding.modal-sync"; isOpen: boolean }
  | { type: "r18-onboarding.preference"; showR18: boolean }
  | { type: "r18-onboarding.confirm" | "r18-onboarding.close" };
export function isR18OnboardingEvent(
  event: TypedEvent,
): event is R18OnboardingEvent {
  return [
    "r18-onboarding.reset",
    "r18-onboarding.dismiss",
    "r18-onboarding.complete",
    "r18-onboarding.modal-sync",
    "r18-onboarding.preference",
    "r18-onboarding.confirm",
    "r18-onboarding.close",
  ].includes(event.type);
}

export interface R18GateState {
  shouldMount: boolean;
  isOpen: boolean;
}
export function createR18GateMediator(
  shouldMount: boolean,
  complete: () => void,
  restoredState?: R18GateState,
): Mediator<R18GateState, R18OnboardingEvent, "complete"> {
  return createMediator<R18GateState, R18OnboardingEvent, "complete">(
    shouldMount
      ? { shouldMount: true, isOpen: restoredState?.isOpen ?? true }
      : { shouldMount: false, isOpen: false },
    (state, event) => {
      switch (event.type) {
        case "r18-onboarding.reset":
          return {
            status: "handled",
            state: { shouldMount: true, isOpen: true },
          };
        case "r18-onboarding.dismiss":
          return { status: "handled", state: { ...state, isOpen: false } };
        case "r18-onboarding.complete":
          return {
            status: "handled",
            state: { ...state, isOpen: false },
            effects: state.isOpen ? ["complete"] : [],
          };
        default:
          return { status: "unhandled" };
      }
    },
    { runEffect: complete },
  );
}

export type R18ModalEffect =
  | { type: "initialize" | "confirm" | "dismiss" }
  | { type: "set-preference"; showR18: boolean };
export function createR18ModalMediator(
  isOpen: boolean,
  runEffect: (effect: R18ModalEffect) => void,
): Mediator<boolean, R18OnboardingEvent, R18ModalEffect> {
  return createMediator<boolean, R18OnboardingEvent, R18ModalEffect>(
    isOpen,
    (state, event) => {
      switch (event.type) {
        case "r18-onboarding.modal-sync":
          return {
            status: "handled",
            state: event.isOpen,
            effects: event.isOpen ? [{ type: "initialize" }] : [],
          };
        case "r18-onboarding.preference":
          return {
            status: "handled",
            state,
            effects: state
              ? [{ type: "set-preference", showR18: event.showR18 }]
              : [],
          };
        case "r18-onboarding.confirm":
          return {
            status: "handled",
            state: false,
            effects: state ? [{ type: "confirm" }] : [],
          };
        case "r18-onboarding.close":
          return {
            status: "handled",
            state: false,
            effects: state ? [{ type: "dismiss" }] : [],
          };
        default:
          return { status: "unhandled" };
      }
    },
    { runEffect },
  );
}
